import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { queueService, type QueueStats, type FailedJob } from '../../services/queue';
import { formatDistanceToNow } from 'date-fns';

/**
 * Queue Management Page (Admin Only)
 *
 * Displays queue statistics and failed job management:
 * - Horizon status
 * - Queue metrics (failed count, recent count)
 * - Wait times by queue priority
 * - Failed jobs list with retry/delete actions
 */
export const QueueManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [failedJobs, setFailedJobs] = useState<FailedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Exception modal state
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<FailedJob | null>(null);

  // Fetch stats and failed jobs
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, failedJobsData] = await Promise.all([
        queueService.getStats(),
        queueService.getFailedJobs(),
      ]);
      setStats(statsData);
      setFailedJobs(failedJobsData.failed_jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('queue_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Retry single job
  const handleRetryJob = async (id: string) => {
    try {
      setActionLoading(id);
      setError(null);
      setSuccess(null);
      await queueService.retryJob(id);
      setSuccess(t('queue_management.messages.retry_success'));
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('queue_management.messages.retry_error'));
    } finally {
      setActionLoading(null);
    }
  };

  // Retry all jobs
  const handleRetryAll = async () => {
    if (!confirm(t('queue_management.confirmations.retry_all'))) {
      return;
    }

    try {
      setActionLoading('retry-all');
      setError(null);
      setSuccess(null);
      await queueService.retryAllJobs();
      setSuccess(t('queue_management.messages.retry_all_success'));
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('queue_management.messages.retry_all_error'));
    } finally {
      setActionLoading(null);
    }
  };

  // Delete single job
  const handleDeleteJob = async (id: string) => {
    if (!confirm(t('queue_management.confirmations.delete_job'))) {
      return;
    }

    try {
      setActionLoading(id);
      setError(null);
      setSuccess(null);
      await queueService.deleteJob(id);
      setSuccess(t('queue_management.messages.delete_success'));
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('queue_management.messages.delete_error'));
    } finally {
      setActionLoading(null);
    }
  };

  // Flush all failed jobs
  const handleFlushAll = async () => {
    if (!confirm(t('queue_management.confirmations.flush_all'))) {
      return;
    }

    try {
      setActionLoading('flush-all');
      setError(null);
      setSuccess(null);
      await queueService.flushFailedJobs();
      setSuccess(t('queue_management.messages.flush_success'));
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('queue_management.messages.flush_error'));
    } finally {
      setActionLoading(null);
    }
  };

  // Open exception modal
  const handleViewException = (job: FailedJob) => {
    setSelectedJob(job);
    setExceptionModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const getStatusBadge = (status: string) => {
    if (status === 'running') {
      return <Badge variant="success">{t('queue_management.status.running')}</Badge>;
    }
    if (status === 'inactive') {
      return <Badge variant="error">{t('queue_management.status.inactive')}</Badge>;
    }
    return <Badge variant="warning">{t('queue_management.status.unknown')}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('queue_management.page_title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('queue_management.page_subtitle')}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={fetchData}
          disabled={loading}
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {t('queue_management.refresh_button')}
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Horizon Status */}
        <Card>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">{t('queue_management.overview.horizon_status')}</h3>
            {getStatusBadge(stats.horizon_status)}
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {t('queue_management.overview.horizon_description')}
          </p>
        </Card>

        {/* Failed Jobs */}
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('queue_management.overview.failed_jobs')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats.failed_count}</p>
          <p className="mt-2 text-xs text-gray-600">
            {t('queue_management.overview.failed_description')}
          </p>
        </Card>

        {/* Recent Jobs */}
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('queue_management.overview.recent_jobs')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats.recent_count}</p>
          <p className="mt-2 text-xs text-gray-600">
            {t('queue_management.overview.recent_description')}
          </p>
        </Card>

        {/* Throughput */}
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('queue_management.overview.throughput')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats.throughput.toFixed(2)}</p>
          <p className="mt-2 text-xs text-gray-600">
            {t('queue_management.overview.throughput_description')}
          </p>
        </Card>
      </div>

      {/* Failed Jobs Section */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('queue_management.failed_jobs_section.title')}</h2>
          {failedJobs.length > 0 && (
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRetryAll}
                disabled={actionLoading === 'retry-all'}
              >
                {actionLoading === 'retry-all' ? t('queue_management.failed_jobs_section.retrying_all') : t('queue_management.failed_jobs_section.retry_all_button')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleFlushAll}
                disabled={actionLoading === 'flush-all'}
              >
                {actionLoading === 'flush-all' ? t('queue_management.failed_jobs_section.deleting_all') : t('queue_management.failed_jobs_section.delete_all_button')}
              </Button>
            </div>
          )}
        </div>

        {failedJobs.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('queue_management.failed_jobs_section.empty_title')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('queue_management.failed_jobs_section.empty_description')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    {t('queue_management.table.job')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    {t('queue_management.table.queue')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    {t('queue_management.table.failed_at')}
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    {t('queue_management.table.exception')}
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    {t('queue_management.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {failedJobs.map((job) => (
                  <tr key={job.id}>
                    <td className="px-3 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        {job.name.split('\\').pop()}
                      </div>
                      <div className="text-xs text-gray-500 font-mono truncate">{t('queue_management.table.id_label')} {job.id.substring(0, 8)}...</div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <Badge variant="default">{job.queue}</Badge>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDistanceToNow(new Date(parseFloat(job.failed_at) * 1000), { addSuffix: true })}
                    </td>
                    <td className="px-3 py-4 text-center">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleViewException(job)}
                        className="text-xs"
                      >
                        {t('queue_management.table.view_exception')}
                      </Button>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRetryJob(job.id)}
                          disabled={actionLoading === job.id}
                        >
                          {actionLoading === job.id ? t('queue_management.table.retrying') : t('queue_management.table.retry_button')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteJob(job.id)}
                          disabled={actionLoading === job.id}
                        >
                          {t('queue_management.table.delete_button')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Exception Details Modal */}
      <Modal
        open={exceptionModalOpen}
        onClose={() => {
          setExceptionModalOpen(false);
          setSelectedJob(null);
        }}
        title={t('queue_management.exception_modal.title')}
      >
        {selectedJob && (
          <div className="space-y-4">
            {/* Job Info */}
            <div className="border-b border-gray-200 pb-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">{t('queue_management.exception_modal.job_label')}</span>{' '}
                  <span className="text-gray-900">{selectedJob.name.split('\\').pop()}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('queue_management.exception_modal.queue_label')}</span>{' '}
                  <span className="text-gray-900">{selectedJob.queue}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('queue_management.exception_modal.id_label')}</span>{' '}
                  <span className="text-gray-900 font-mono text-xs">{selectedJob.id}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('queue_management.exception_modal.failed_label')}</span>{' '}
                  <span className="text-gray-900">
                    {formatDistanceToNow(new Date(parseFloat(selectedJob.failed_at) * 1000), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>

            {/* Exception */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('queue_management.exception_modal.stack_trace_title')}</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto max-h-96">
                <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
                  {selectedJob.exception}
                </pre>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setExceptionModalOpen(false);
                  handleRetryJob(selectedJob.id);
                }}
              >
                {t('queue_management.exception_modal.retry_button')}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setExceptionModalOpen(false);
                  handleDeleteJob(selectedJob.id);
                }}
              >
                {t('queue_management.exception_modal.delete_button')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

QueueManagementPage.displayName = 'QueueManagementPage';
